import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { MdAdd, MdPrint, MdRefresh } from "react-icons/md";
import { TbTruckDelivery } from "react-icons/tb";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { replaceCharacter } from "../components";
import CalendarMonth, {
  startOfDay,
  shiftDays,
  shiftMonth,
  monthOf,
} from "../components/CalendarMonth";
import RunDetailModal from "../components/RunDetailModal";
import { fetchDriverDeliveryStats } from "./deliveryDriverStats";
import { openRunBuilder } from "../runBuilderWindow";
import "../delivery-runs.css";

/* ------------------------------------------------------------------ *
 * Delivery runs (redesign) — /delivery-runs
 *
 * Replaces src/pages/deliveryRunList.js (kept at /DeliveryRunList-v1):
 *   - a 5-column strip that squeezed description and comment into 40
 *     characters              -> one card per run, every field readable
 *   - a half-width column that
 *     collapsed when a run
 *     was open                -> the list keeps its layout, the run
 *                                opens in RunDetailModal
 *   - a date filter with no
 *     search                  -> date filter + the byQuery search the
 *                                endpoint already supported
 *   - (new)                   -> day totals, stop/product counts per
 *                                run, a "New run sheet" entry point
 *
 * Same endpoints and query shape as the legacy page: POST /run/getAllBy
 * { page, byQuery, limit, sortBy, find }, POST /run/removeRun (in the
 * modal), and the same print URLs — /deliveries/print/<filter JSON> for
 * a whole day, /run-sheet/print/:id for one run.
 *
 * Date range (defaults to today + the next 6 days): `find` goes straight to Mongo, so
 * a range is sent as delivery_date: { $in: [every DD/MM/YYYY in it] } —
 * the same strings the single-day filter always matched. The endpoint's
 * responseLength ignores `find` (it counts the whole business), so a
 * ranged list is fetched whole and paged here; "All dates" keeps the
 * server paging.
 * ------------------------------------------------------------------ */

const DATE_FMT = "DD/MM/YYYY";
const PAGE_LIMIT = 15;
const RANGE_LIMIT = 1000;

function monthRange(offset) {
  const m = moment().add(offset, "month");
  return {
    from: startOfDay(m.clone().startOf("month").valueOf()),
    to: startOfDay(m.clone().endOf("month").valueOf()),
  };
}

/* Today and the six days after it — what the office plans runs against. */
function nextWeekRange() {
  const t = startOfDay(Date.now());
  return { from: t, to: shiftDays(t, 6) };
}

const PRESETS = [
  { label: "Next 7 days", get: nextWeekRange },
  {
    label: "Today",
    get: () => {
      const t = startOfDay(Date.now());
      return { from: t, to: t };
    },
  },
  {
    label: "This week",
    get: () => ({
      from: startOfDay(moment().startOf("isoWeek").valueOf()),
      to: startOfDay(moment().endOf("isoWeek").valueOf()),
    }),
  },
  { label: "This month", get: () => monthRange(0) },
  { label: "Last month", get: () => monthRange(-1) },
];

function daysIn(from, to) {
  const out = [];
  for (let t = from; t <= to; t = shiftDays(t, 1)) {
    out.push(moment(t).format(DATE_FMT));
  }
  return out;
}

/* Ordered so the first few are the easiest to tell apart — most days only
   have two or three drivers out. */
const BAR_COLORS = ["var(--rf-brand)", "var(--rf-accent)", "#4d7a52", "#a8483e", "#7a5b8f", "#b0872f"];

/* Colours are handed out by the driver's rank among the drivers on this
   page, not by hashing the name — hashing happily gives two drivers the
   same colour, which is exactly the case the colour exists to tell apart.
   One driver keeps one colour down the whole list. */
function buildDriverColors(list) {
  const map = {};
  let n = 0;
  list.forEach((r) => {
    const key = String(r.drivers_name || "").trim().toLowerCase();
    if (key && map[key] === undefined) {
      map[key] = BAR_COLORS[n % BAR_COLORS.length];
      n += 1;
    }
  });
  return map;
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many || one + "s"}`;

const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

/* One mark per stop along a dashed line that runs on to the return pin at
   the right — a card's route reads as long or short at a glance. Past
   ROUTE_MAX the tail becomes a "+n". */
const ROUTE_MAX = 10;

function RouteLine({ stops }) {
  const shown = Math.min(stops, ROUTE_MAX);
  return (
    <div className="dr-route" aria-hidden="true">
      <span className="dr-route-start" />
      {Array.from({ length: shown }, (_, i) => (
        <span className="dr-stop-dot" key={i} />
      ))}
      {stops > ROUTE_MAX && (
        <span className="dr-route-more">+{stops - ROUTE_MAX}</span>
      )}
      <span className="dr-route-end" />
    </div>
  );
}

const safeParse = (raw, fallback) => {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

/* Stops = deliveries merged per funeral + destination (or per retail work
   order), matching the run sheet itself. Counting here keeps the cards
   honest about how big a run is. */
function countRun(run) {
  const keys = new Set();
  let products = 0;
  (run.deliveries || []).forEach((el) => {
    const loc = safeParse(el.location, {}) || {};
    const c = loc.coordinates;
    const place = c
      ? `${c.lat}_${c.lng}`
      : String(loc.name || "").replace(/\s+/g, "").toLowerCase();
    keys.add(
      el.order_type === "Funeral"
        ? `${el.parent_funeral_id}@${place}`
        : `wo-${el.work_order_id}`
    );
    products += (safeParse(el.products, []) || []).length;
  });
  return { stops: keys.size, products };
}

/* "Today" / "Tomorrow" / "Thu 18 Sep" for a DD/MM/YYYY delivery date. */
function dayLabel(date) {
  const m = moment(date, DATE_FMT, true);
  if (!m.isValid()) return date || "No date";
  const days = m.startOf("day").diff(moment().startOf("day"), "days");
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days === -1) return "Yesterday";
  return m.format("ddd D MMM YYYY");
}

export function DeliveryRunsRedesign() {
  const navigate = useNavigate();

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [pages, setPages] = useState(0);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");

  /* { from, to } in local-midnight stamps, or null for all dates. */
  const [range, setRange] = useState(nextWeekRange);
  const [preset, setPreset] = useState("Next 7 days");

  const [calOpen, setCalOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(null);
  const [draftTo, setDraftTo] = useState(null);
  const [viewMonth, setViewMonth] = useState(() => monthOf(Date.now()));
  const calRef = useRef(null);

  const [driverStats, setDriverStats] = useState([]);
  const [driverLoading, setDriverLoading] = useState(true);

  const rangeDays = useMemo(
    () => (range ? daysIn(range.from, range.to) : null),
    [range]
  );
  const singleDay = rangeDays && rangeDays.length === 1 ? rangeDays[0] : "";

  const [openRun, setOpenRun] = useState(null);

  /* Type-ahead search, debounced onto the endpoint's byQuery. */
  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  const fetchRuns = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    axios
      .post(BASE_SERVER_URL + "/run/getAllBy", {
        page: rangeDays ? 1 : page,
        byQuery: query,
        limit: rangeDays ? RANGE_LIMIT : PAGE_LIMIT,
        sortBy: "delivery_date",
        find: rangeDays ? { delivery_date: { $in: rangeDays } } : {},
      })
      .then((doc) => {
        if (doc.data.status !== "success") {
          setLoading(false);
          setLoadError(true);
          return;
        }
        const list = doc.data.response || [];
        const count = rangeDays ? list.length : doc.data.responseLength || 0;
        setRuns(list);
        setTotal(count);
        setPages(Math.ceil(count / PAGE_LIMIT));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setLoadError(true);
      });
    // a ranged list is paged locally, so a page change needn't refetch it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeDays ? 0 : page, query, rangeDays]);

  useEffect(fetchRuns, [fetchRuns]);

  /* Products delivered per driver for the chosen range (GET /run/driver-stats). */
  useEffect(() => {
    let live = true;
    setDriverLoading(true);
    fetchDriverDeliveryStats({
      startDate: rangeDays ? rangeDays[0] : null,
      endDate: rangeDays ? rangeDays[rangeDays.length - 1] : null,
    })
      .then((rows) => {
        if (!live) return;
        setDriverStats(
          [...(rows || [])].sort((a, b) => b.products - a.products)
        );
        setDriverLoading(false);
      })
      .catch(() => {
        if (!live) return;
        setDriverStats([]);
        setDriverLoading(false);
      });
    return () => {
      live = false;
    };
  }, [rangeDays]);

  /* The runs on screen: a slice of the ranged list, or the server page. */
  const pageRuns = useMemo(
    () =>
      rangeDays ? runs.slice((page - 1) * PAGE_LIMIT, page * PAGE_LIMIT) : runs,
    [runs, rangeDays, page]
  );

  /* Calendar popover: close on outside click or Escape. */
  useEffect(() => {
    if (!calOpen) return undefined;
    const onDown = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) setCalOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setCalOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [calOpen]);

  /* Runs keep the server's delivery_date order; group consecutive runs of
     the same day under one heading — the legacy TableRowSeparator. */
  const groups = useMemo(() => {
    const out = [];
    pageRuns.forEach((run, i) => {
      const date = run.delivery_date || "";
      const entry = { run, num: i + 1 };
      const last = out[out.length - 1];
      if (last && last.date === date) last.runs.push(entry);
      else out.push({ date, runs: [entry] });
    });
    return out;
  }, [pageRuns]);

  const counts = useMemo(() => {
    const map = {};
    runs.forEach((r) => {
      map[r._id] = countRun(r);
    });
    return map;
  }, [runs]);

  const driverColors = useMemo(() => buildDriverColors(runs), [runs]);
  const colorOf = (run) =>
    driverColors[String(run.drivers_name || "").trim().toLowerCase()] ||
    BAR_COLORS[0];

  const pageStats = useMemo(() => {
    let stops = 0;
    let products = 0;
    const drivers = new Set();
    runs.forEach((r) => {
      const c = counts[r._id] || { stops: 0, products: 0 };
      stops += c.stops;
      products += c.products;
      if (r.drivers_name) drivers.add(r.drivers_name.trim().toLowerCase());
    });
    return { stops, products, drivers: drivers.size };
  }, [runs, counts]);

  function printDay() {
    window.open(
      "/deliveries/print/" +
        JSON.stringify({
          page: 1,
          byQuery: query,
          limit: PAGE_LIMIT,
          sortBy: "delivery_date",
          find: { delivery_date: replaceCharacter(singleDay, "/", "-") },
        }),
      "_blank"
    );
  }

  /* ---------------- date range controls ---------------- */
  function openCal() {
    setDraftFrom(range ? range.from : null);
    setDraftTo(range ? range.to : null);
    setViewMonth(monthOf(range ? range.to : Date.now()));
    setCalOpen(true);
  }

  function applyRange(next, label) {
    setRange(next);
    setPreset(label);
    setPage(1);
    setCalOpen(false);
  }

  function pickDay(stamp) {
    if (!draftFrom || draftTo) {
      setDraftFrom(stamp);
      setDraftTo(null);
    } else if (stamp < draftFrom) {
      setDraftTo(draftFrom);
      setDraftFrom(stamp);
    } else {
      setDraftTo(stamp);
    }
  }

  const leftMonth = shiftMonth(viewMonth, -1);
  const rangeLabel = !range
    ? "All dates"
    : range.from === range.to
    ? moment(range.from).format("ddd D MMM YYYY")
    : preset === "This month" || preset === "Last month"
    ? moment(range.from).format("MMMM YYYY")
    : `${moment(range.from).format("D MMM")} – ${moment(range.to).format(
        "D MMM YYYY"
      )}`;
  const scopeLabel = rangeDays ? "in range" : "on this page";

  const driverMax = driverStats.reduce((m, r) => Math.max(m, r.products), 0);
  const driverTotal = driverStats.reduce((n, r) => n + r.products, 0);

  /* Page numbers, windowed around the current page. */
  const pageList = useMemo(() => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const out = [1];
    const from = Math.max(2, page - 1);
    const to = Math.min(pages - 1, page + 1);
    if (from > 2) out.push("…");
    for (let i = from; i <= to; i += 1) out.push(i);
    if (to < pages - 1) out.push("…");
    out.push(pages);
    return out;
  }, [pages, page]);

  return (
    <div className="dr-page">
      {/* ---------------- toolbar ---------------- */}
      <div className="dr-bar">
        <div className="dr-cal-wrap" ref={calRef}>
          <button
            type="button"
            className={`dr-date-btn${calOpen ? " is-open" : ""}${
              range ? " is-set" : ""
            }`}
            aria-expanded={calOpen}
            onClick={() => (calOpen ? setCalOpen(false) : openCal())}
          >
            <span className="dr-date-k">Dates</span>
            <span className="rf-num">{rangeLabel}</span>
            {rangeDays && rangeDays.length > 1 && (
              <span className="dr-range-days rf-num">
                {plural(rangeDays.length, "day")}
              </span>
            )}
            <span className="dr-caret">▾</span>
          </button>

          {calOpen && (
            <div className="dr-cal">
              <div className="dr-presets">
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    className={range && preset === p.label ? "is-active" : ""}
                    onClick={() => applyRange(p.get(), p.label)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="dr-cal-months">
                <CalendarMonth
                  year={leftMonth.year}
                  monthIndex={leftMonth.monthIndex}
                  from={draftFrom}
                  to={draftTo}
                  onPick={pickDay}
                  onPrev={() => setViewMonth((m) => shiftMonth(m, -1))}
                />
                <CalendarMonth
                  year={viewMonth.year}
                  monthIndex={viewMonth.monthIndex}
                  from={draftFrom}
                  to={draftTo}
                  onPick={pickDay}
                  onNext={() => setViewMonth((m) => shiftMonth(m, 1))}
                />
              </div>
              <div className="dr-cal-foot">
                <button
                  type="button"
                  className="rf-btn rf-btn-ghost"
                  onClick={() => applyRange(null, null)}
                >
                  All dates
                </button>
                <span className="dr-cal-hint">
                  {draftFrom && draftTo
                    ? `${moment(draftFrom).format("D MMM")} – ${moment(
                        draftTo
                      ).format("D MMM YYYY")}`
                    : draftFrom
                    ? "Pick the end of the range"
                    : "Pick a start date"}
                </span>
                <button
                  type="button"
                  className="rf-btn rf-btn-ghost"
                  onClick={() => setCalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rf-btn rf-btn-primary"
                  disabled={!draftFrom || !draftTo}
                  onClick={() =>
                    applyRange({ from: draftFrom, to: draftTo }, null)
                  }
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        <label className="dr-search">
          <span className="dr-search-icon" />
          <input
            type="search"
            value={search}
            placeholder="Search driver, vehicle, description"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>

        <span className="rf-spacer" />

        <button
          type="button"
          className="rf-btn rf-btn-ghost"
          onClick={fetchRuns}
          title="Reload"
        >
          <MdRefresh style={{ verticalAlign: "-2px" }} /> Reload
        </button>
        {singleDay !== "" && (
          <button type="button" className="rf-btn rf-btn-ghost" onClick={printDay}>
            <MdPrint style={{ verticalAlign: "-2px" }} /> Print day
          </button>
        )}
        <button
          type="button"
          className="rf-btn rf-btn-primary"
          onClick={() => navigate("/run-sheet/new")}
        >
          <MdAdd style={{ verticalAlign: "-2px" }} /> New run sheet
        </button>
      </div>

      {/* ---------------- KPI strip ---------------- */}
      <div className="dr-kpis">
        <div className="dr-kpi">
          <span className="dr-kpi-k">Run sheets</span>
          <span className="dr-kpi-v">{total}</span>
          <span className="dr-kpi-sub">{rangeLabel}</span>
        </div>
        <div className="dr-kpi">
          <span className="dr-kpi-k">Drivers</span>
          <span className="dr-kpi-v">{pageStats.drivers}</span>
          <span className="dr-kpi-sub">{scopeLabel}</span>
        </div>
        <div className="dr-kpi">
          <span className="dr-kpi-k">Stops</span>
          <span className="dr-kpi-v">{pageStats.stops}</span>
          <span className="dr-kpi-sub">{scopeLabel}</span>
        </div>
        <div className="dr-kpi">
          <span className="dr-kpi-k">Products</span>
          <span className="dr-kpi-v">{pageStats.products}</span>
          <span className="dr-kpi-sub">{scopeLabel}</span>
        </div>
      </div>

      {/* ---------------- list ---------------- */}
      <div className="dr-body">
        {/* products delivered, per driver, for the chosen range */}
        <section className="dr-drivers">
          <div className="dr-drivers-head">
            <span className="dr-drivers-title">Products delivered by driver</span>
            <span className="dr-drivers-range rf-num">{rangeLabel}</span>
            <span className="rf-spacer" />
            {!driverLoading && driverStats.length > 0 && (
              <span className="dr-drivers-total rf-num">
                {plural(driverTotal, "product")}
              </span>
            )}
          </div>

          {driverLoading ? (
            <div className="dr-drivers-empty">Loading…</div>
          ) : driverStats.length === 0 ? (
            <div className="dr-drivers-empty">No deliveries in this range.</div>
          ) : (
            <div className="dr-drivers-list">
              {driverStats.map((d, i) => (
                <div
                  className="dr-driver"
                  key={d.driver}
                  style={{ "--dr-key": BAR_COLORS[i % BAR_COLORS.length] }}
                >
                  <span className="dr-driver-face">{initials(d.driver)}</span>
                  <span className="dr-driver-main">
                    <span className="dr-driver-line">
                      <span className="dr-driver-name">{d.driver}</span>
                      <span className="dr-driver-n rf-num">{d.products}</span>
                    </span>
                    <span className="dr-driver-track">
                      <span
                        className="dr-driver-fill"
                        style={{
                          width: `${driverMax ? (d.products / driverMax) * 100 : 0}%`,
                        }}
                      />
                    </span>
                    <span className="dr-driver-sub rf-num">
                      {plural(d.runs, "run")} · {plural(d.stops, "stop")}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {loading && <div className="rf-empty">Loading run sheets…</div>}

        {!loading && loadError && (
          <div className="rf-empty">
            Couldn&apos;t load run sheets.{" "}
            <button type="button" className="rf-btn rf-btn-ghost" onClick={fetchRuns}>
              Retry
            </button>
          </div>
        )}

        {!loading && !loadError && runs.length === 0 && (
          <div className="rf-empty">
            No run sheet found
            {range ? ` for ${rangeLabel}` : ""}
            {query ? ` matching “${query}”` : ""}.
          </div>
        )}

        {!loading &&
          !loadError &&
          groups.map((group, gi) => (
            <section className="dr-daygroup" key={`${group.date}-${gi}`}>
              <div className="dr-dayhead">
                <span className="dr-dayhead-label">{dayLabel(group.date)}</span>
                <span className="dr-dayhead-rule" />
                <span className="dr-dayhead-count rf-num">
                  {plural(group.runs.length, "run")}
                </span>
              </div>

              <div className="dr-grid">
                {group.runs.map(({ run, num }, i) => {
                  const c = counts[run._id] || { stops: 0, products: 0 };
                  return (
                    <button
                      type="button"
                      key={run._id || i}
                      className={`dr-card${
                        openRun && openRun._id === run._id ? " is-active" : ""
                      }`}
                      style={{ "--dr-key": colorOf(run) }}
                      onClick={() => setOpenRun(run)}
                    >
                      <div className="dr-top">
                        <span
                          className={`dr-zone${run.description ? "" : " is-empty"}`}
                          title={run.description || ""}
                        >
                          {run.description || "No route given"}
                        </span>
                        <span className="dr-runno">
                          {String((page - 1) * PAGE_LIMIT + num).padStart(2, "0")}
                        </span>
                      </div>

                      <div className="dr-who">
                        <span className="dr-face">
                          <TbTruckDelivery />
                        </span>
                        <span className="dr-who-text">
                          <span className="dr-name">
                            {run.drivers_name || "Unnamed driver"}
                          </span>
                          {run.vehicle_number && (
                            <span className="dr-plate">{run.vehicle_number}</span>
                          )}
                        </span>
                      </div>

                      {/* only runs that carry a note show one — most don't */}
                      {run.comment && <div className="dr-note">{run.comment}</div>}

                      <div className="dr-load">
                        <span className="dr-stat">
                          <span className="dr-stat-n">{c.stops}</span>
                          <span className="dr-stat-k">
                            {c.stops === 1 ? "stop" : "stops"}
                          </span>
                        </span>
                        <span className="dr-stat">
                          <span className="dr-stat-n">{c.products}</span>
                          <span className="dr-stat-k">
                            {c.products === 1 ? "product" : "products"}
                          </span>
                        </span>
                      </div>

                      <RouteLine stops={c.stops} />
                    </button>
                  );
                })}
              </div>
            </section>
          ))}

        {!loading && !loadError && pages > 1 && (
          <div className="dr-pager">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ‹
            </button>
            {pageList.map((p, i) =>
              p === "…" ? (
                <button type="button" key={`gap-${i}`} disabled>
                  …
                </button>
              ) : (
                <button
                  type="button"
                  key={p}
                  className={p === page ? "is-active" : ""}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              )
            )}
            <button
              type="button"
              disabled={page === pages}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
            >
              ›
            </button>
          </div>
        )}
      </div>

      {openRun && (
        <RunDetailModal
          run={openRun}
          onClose={() => setOpenRun(null)}
          onEdit={() => {
            /* Like "Make run sheet": the builder pops out on this run and
               this tab becomes the work order list on the run's day with
               its stops picked, so more can be added. Opened here, inside
               the click, so the browser lets the pop-up through. */
            if (!openRunBuilder("/run-sheet/" + openRun._id + "/edit")) {
              toast.error(
                "The run sheet window was blocked — allow pop-ups for this site, then press “Show run sheet”."
              );
            }
            navigate("/work-orders?editRun=" + openRun._id);
          }}
          onDeleted={() => {
            toast.success("Run sheet deleted.");
            setOpenRun(null);
            fetchRuns();
          }}
        />
      )}
    </div>
  );
}

export default DeliveryRunsRedesign;
